import { Alert, Box, Button, Typography } from '@mui/material';
import type { Permission } from '@nlc/contracts';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useRouteError } from 'react-router';

import { hasPermission, sessionQuery } from './auth/session.js';
import { LoadingState } from './components/states.js';
import AppShell from './layout/app-shell.js';

const LoginPage = lazy(() => import('./auth/login-page.js'));
const DashboardPage = lazy(() => import('./features/dashboard/dashboard-page.js'));
const DocumentsPage = lazy(() => import('./features/documents/documents-page.js'));
const ReportsLandingPage = lazy(() => import('./features/reports/reports-landing-page.js'));
const ReportPage = lazy(() => import('./features/reports/report-page.js'));
const PortfolioPage = lazy(() => import('./features/reports/portfolio-page.js'));
const T12Page = lazy(() => import('./features/reports/t12-page.js'));
const MonthClosePage = lazy(() => import('./features/reports/month-close-page.js'));
const NotesPage = lazy(() => import('./features/reports/notes-page.js'));
const BudgetLinesPage = lazy(() => import('./features/reports/budget-lines-page.js'));
const ManagerBudgetPage = lazy(() => import('./features/reports/manager-budget-page.js'));
const MoveConfirmationPage = lazy(() => import('./features/reports/move-confirmation-page.js'));
const ReservationsPage = lazy(() => import('./features/reservations/reservations-page.js'));
const MembersPage = lazy(() => import('./features/admin/members-page.js'));
const SecurityPage = lazy(() => import('./features/admin/security-page.js'));
const IntegrationsPage = lazy(() => import('./features/admin/integrations-page.js'));
const OrganizationPage = lazy(() => import('./features/admin/organization-page.js'));

function withSuspense(component: React.ReactNode): React.JSX.Element {
  return <Suspense fallback={<LoadingState label="Loading page" />}>{component}</Suspense>;
}

/**
 * A page the member's role does not reach goes back to Reports instead of
 * rendering the API's refusal. The API refuses it either way: this is the
 * courtesy, not the control.
 */
function RequirePermission({
  permission,
  children,
}: {
  readonly permission: Permission;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const session = useQuery(sessionQuery);
  if (!session.data) return <LoadingState label="Loading your session" />;
  return hasPermission(session.data, permission) ? (
    <>{children}</>
  ) : (
    <Navigate to="/reports" replace />
  );
}

function guarded(permission: Permission, component: React.ReactNode): React.JSX.Element {
  return withSuspense(<RequirePermission permission={permission}>{component}</RequirePermission>);
}

function RouteError(): React.JSX.Element {
  const error = useRouteError();
  return (
    <Box component="main" sx={{ p: 4 }}>
      <Typography component="h1" variant="h1" sx={{ mb: 2 }}>
        This page could not be displayed
      </Typography>
      <Alert severity="error">
        {error instanceof Error ? error.message : 'An unexpected routing error occurred.'}
      </Alert>
      <Button href="/" sx={{ mt: 2 }}>
        Return to dashboard
      </Button>
    </Box>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: withSuspense(<LoginPage />), errorElement: <RouteError /> },
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: withSuspense(<ReservationsPage />) },
      { path: 'dashboard', element: withSuspense(<DashboardPage />) },
      { path: 'documents', element: withSuspense(<DocumentsPage />) },
      { path: 'reservations', element: <Navigate to="/" replace /> },
      { path: 'reports', element: withSuspense(<ReportsLandingPage />) },
      { path: 'reports/portfolio', element: guarded('reports.finance', <PortfolioPage />) },
      { path: 'reports/t12', element: guarded('reports.finance', <T12Page />) },
      { path: 'reports/budget', element: guarded('reports.budget', <ManagerBudgetPage />) },
      { path: 'reports/:kind', element: withSuspense(<ReportPage />) },
      { path: 'notes', element: guarded('reports.finance', <NotesPage />) },
      { path: 'month-close', element: guarded('reports.reconcile', <MonthClosePage />) },
      { path: 'moves', element: guarded('months.close', <MoveConfirmationPage />) },
      {
        path: 'admin/budget-lines',
        element: guarded('organizations.manage', <BudgetLinesPage />),
      },
      { path: 'admin/members', element: withSuspense(<MembersPage />) },
      { path: 'admin/security', element: withSuspense(<SecurityPage />) },
      { path: 'admin/integrations', element: withSuspense(<IntegrationsPage />) },
      { path: 'admin/organization', element: withSuspense(<OrganizationPage />) },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App(): React.JSX.Element {
  return <RouterProvider router={router} />;
}
